import {
  useWSModalCustom,
  WSLoader,
  WSModal,
  WSPage
} from "@wingspanhq/fe-component-library";
import {
  PayeeRequirement,
  RequirementType
} from "@wingspanhq/wingspan-api-sdk";
import { useEffect } from "react";

import { ErrorAlert } from "@components/ErrorAlert";
import { queriesInsuranceMonitors } from "../../../../v3/query/queriesInsuranceMonitors";
import { queriesRequirements } from "../../../../v3/query/queriesRequirements";
import { queriesSharedFileRequests } from "../../../../v3/query/queriesSharedFileRequests";
import { getRequirementDefinitionTemplateByParams } from "../../../RequirementDefinitions/utils";
import { IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS } from "../../modules/IdentityVerification/instructions";
import { INSURANCE_MONITOR_EXPAND } from "../../modules/InsuranceCoverage/constants";
import { RequirementContextType } from "../../types";
import { useRequirementsConfig } from "../../useRequirementsConfig";

import { RequirementDetailsInstructions } from "./RequirementDetailsInstructions";

type RequirementInstructionsModalProps = {
  onClose: (result?: boolean) => void;
  requirementId: string;
};
type RequirementInstructionsModalContentProps = {
  onClose: (result?: boolean) => void;
  requirement: PayeeRequirement;
};

const InstructionsModalContent: React.FC<
  RequirementInstructionsModalContentProps
> = ({ requirement, onClose }) => {
  const requirementConfig = useRequirementsConfig({
    requirements: [requirement],
    contextType: RequirementContextType.Payer
  });

  /**
   * Sourced from the requirement's own data source, not from the definition it
   * was materialized from: this modal runs on the *contractor's* seat, where the
   * payer-owned `RequirementDefinition` — and the
   * `SharedFileRequestConfiguration` behind it — both 404. The materialized
   * `SharedFileRequest` is visible to both sides and carries the same
   * `instructions` / `contextType`, so the upload instructions come off the
   * instance. Shares its key with the read `useSharedFileRequirementTableConfig`
   * already makes, so this costs no extra request.
   */
  const querySharedFileRequest =
    queriesSharedFileRequests.getSharedFileRequest.useQuery(
      { sharedFileRequestId: requirement.dataSourceId! },
      {
        enabled:
          !!requirement.dataSourceId &&
          requirement.type === RequirementType.DOCUMENT_UPLOAD
      }
    );

  const sharedFileRequest = querySharedFileRequest.data;

  const queryMonitor = queriesInsuranceMonitors.getInsuranceMonitor.useQuery(
    {
      monitorId: requirement.dataSourceId!,
      expand: INSURANCE_MONITOR_EXPAND
    },
    {
      enabled:
        !!requirement.dataSourceId &&
        requirement.type === RequirementType.INSURANCE_COVERAGE
    }
  );

  useEffect(() => {
    if (
      requirement.type === RequirementType.DOCUMENT_UPLOAD &&
      querySharedFileRequest.isSuccess &&
      querySharedFileRequest.data
    ) {
      const template = getRequirementDefinitionTemplateByParams({
        type: RequirementType.DOCUMENT_UPLOAD,
        contextType: querySharedFileRequest.data.contextType
      });

      const isInstructionsExist =
        !!template.instructions || !!querySharedFileRequest.data.instructions;

      if (!isInstructionsExist) {
        onClose(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [querySharedFileRequest.dataUpdatedAt, requirement.type, onClose]);

  if (requirement.type === RequirementType.BIOMETRIC_IDENTITY_VERIFICATION) {
    return (
      <WSModal
        fullScreen
        showCloseIcon={true}
        title={requirementConfig.getRequirementName(requirement)}
        titleAlign="center"
        onClose={onClose}
      >
        <WSPage>
          <RequirementDetailsInstructions
            {...requirementConfig}
            contextType={RequirementContextType.Payer}
            instructions={IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS}
            requirement={requirement}
            onCloseAction={() => onClose(false)}
            onSuccessAction={() => onClose(true)}
          />
        </WSPage>
      </WSModal>
    );
  } else if (requirement.type === RequirementType.DOCUMENT_UPLOAD) {
    const template = getRequirementDefinitionTemplateByParams({
      type: RequirementType.DOCUMENT_UPLOAD,
      contextType: sharedFileRequest?.contextType
    });

    const instructions =
      template.instructions || sharedFileRequest?.instructions || "";

    return (
      <WSModal
        fullScreen
        showCloseIcon
        title={requirementConfig.getRequirementName(requirement)}
        titleAlign="center"
        onClose={onClose}
      >
        <WSPage>
          {querySharedFileRequest.isLoading &&
          !querySharedFileRequest.isDisabled ? (
            <WSLoader.Spinner size="L" />
          ) : (
            <RequirementDetailsInstructions
              {...requirementConfig}
              contextType={RequirementContextType.Payer}
              instructions={instructions}
              requirement={requirement}
              onCloseAction={() => onClose(false)}
              onSuccessAction={() => onClose(true)}
            />
          )}
        </WSPage>
      </WSModal>
    );
  } else if (requirement.type === RequirementType.INSURANCE_COVERAGE) {
    return (
      <WSModal
        fullScreen
        // Always closable: gating the close icon on a loading flag turns any
        // read that never settles into a modal the contractor cannot leave.
        showCloseIcon
        title={requirementConfig.getRequirementName(requirement)}
        titleAlign="center"
        onClose={onClose}
      >
        <WSPage>
          {requirementConfig.isLoading ||
          (queryMonitor.isLoading && !queryMonitor.isDisabled) ? (
            <WSLoader.Spinner size="L" />
          ) : requirementConfig.getRequirementInstructions(requirement) ? (
            <RequirementDetailsInstructions
              {...requirementConfig}
              contextType={RequirementContextType.Payer}
              instructions={""}
              monitor={queryMonitor.data}
              requirement={requirement}
              onCloseAction={() => onClose(false)}
              onSuccessAction={() => onClose(true)}
            />
          ) : null}
        </WSPage>
      </WSModal>
    );
  } else {
    return (
      <WSModal
        allowClickOutsideToClose={false}
        fullScreen
        title="Error"
        titleAlign="center"
        onClose={onClose}
      >
        <ErrorAlert context="Other" error="Something went wrong" />
      </WSModal>
    );
  }
};

const RequirementInstructionsModal: React.FC<
  RequirementInstructionsModalProps
> = ({ onClose, requirementId }) => {
  const queryRequirement = queriesRequirements.getPayeeRequirement.useQuery({
    requirementId
  });

  if (queryRequirement.isLoading) {
    return (
      <WSModal
        allowClickOutsideToClose={false}
        fullScreen
        showCloseIcon={false}
        title="Loading..."
        titleAlign="center"
      >
        <WSLoader.Spinner size="M" />
      </WSModal>
    );
  } else if (queryRequirement.isError) {
    return (
      <WSModal
        allowClickOutsideToClose={false}
        fullScreen
        title="Error"
        titleAlign="center"
        onClose={onClose}
      >
        <ErrorAlert context="Other" error="Something went wrong" />
      </WSModal>
    );
  } else {
    return (
      <InstructionsModalContent
        requirement={queryRequirement.data}
        onClose={onClose}
      />
    );
  }
};

export const useRequirementInstructionsModal = () =>
  useWSModalCustom(RequirementInstructionsModal);
